import React from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, RefreshCw, X } from 'lucide-react';

/**
 * CSV Import Modal Component
 * Modal for importing customers from CSV files
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.importFile - Selected CSV file object
 * @param {Function} props.setImportFile - Function to set the import file
 * @param {Array} props.importPreview - Array of previewed CSV rows
 * @param {Function} props.setImportPreview - Function to set the preview data
 * @param {Object} props.columnMapping - Mapping of CSV columns to customer fields
 * @param {Function} props.setColumnMapping - Function to update column mapping
 * @param {boolean} props.isImporting - Whether import is in progress
 * @param {Function} props.onFileUpload - Function to handle file upload
 * @param {Function} props.onImport - Function to handle bulk import
 * @param {Object} props.theme - Theme object with styling properties
 * @param {Object} props.colors - Color palette object
 */
export const CSVImportModal = ({
  show,
  onClose,
  importFile,
  setImportFile,
  importPreview,
  setImportPreview,
  columnMapping,
  setColumnMapping,
  isImporting,
  onFileUpload,
  onImport,
  theme,
  colors
}) => {
  if (!show) return null;

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0, 0, 0, 0.8)', 
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000,
      padding: '20px',
      animation: 'fadeIn 0.3s ease-out'
    }}>
      <div style={{ 
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%)',
        backdropFilter: 'blur(40px) saturate(150%)',
        WebkitBackdropFilter: 'blur(40px) saturate(150%)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
        padding: '40px', 
        width: '95%', 
        maxWidth: '1400px', 
        maxHeight: '90vh', 
        overflowY: 'auto',
        animation: 'scaleIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden'
      }}>
        {/* Top highlight */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)'
        }} />
        
        {/* Inner glow */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.08), transparent 50%)',
          pointerEvents: 'none'
        }} />
        
        {/* Modal Header */}
        <div style={{ 
          position: 'relative',
          zIndex: 1,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '32px', 
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)', 
          paddingBottom: '24px' 
        }}>
          <h2 style={{ 
            color: '#ffffff', 
            margin: 0, 
            fontSize: '32px', 
            fontWeight: '600',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
            textShadow: '0 0 40px rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Upload size={24} style={{ color: '#3b82f6' }} /> Import Customers from CSV
          </h2>
          <button 
            onClick={onClose} 
            style={{ 
              width: '40px',
              height: '40px',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Step 1: File Upload */}
        {!importFile && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{
              border: '3px dashed rgba(102, 126, 234, 0.4)',
              borderRadius: '16px',
              padding: '60px 40px',
              background: 'rgba(102, 126, 234, 0.05)',
              marginBottom: '20px'
            }}>
              <Upload size={64} style={{ color: colors.primary, marginBottom: '20px' }} />
              <h3 style={{ color: theme.text, marginBottom: '10px' }}>
                Upload CSV File
              </h3>
              <p style={{ color: theme.textSecondary, marginBottom: '20px' }}>
                Drag and drop your CSV file here, or click to browse
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => onFileUpload(e.target.files[0])}
                style={{ display: 'none' }}
                id="csv-file-input"
              />
              <label 
                htmlFor="csv-file-input"
                style={{
                  display: 'inline-block',
                  padding: '12px 30px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Select CSV File
              </label>
            </div>

            <div style={{ 
              ...theme.glass, 
              padding: '20px', 
              borderRadius: '12px', 
              textAlign: 'left',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <h4 style={{ color: colors.primary, marginBottom: '15px' }}>
                <FileText size={18} style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} /> CSV Format Requirements:
              </h4>
              <ul style={{ color: theme.textSecondary, lineHeight: '1.8' }}>
                <li>First row should contain column headers</li>
                <li><strong>Required:</strong> First Name, Last Name</li>
                <li><strong>Optional:</strong> Email, Phone, Address, City, State, Zip</li>
                <li><strong>Vehicle Info:</strong> Year, Make, Model, VIN, Mileage</li>
                <li><strong>Service Info:</strong> Last Service Date, Service Type</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 2: Column Mapping & Preview */}
        {importFile && importPreview.length > 0 && (
          <div>
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ color: colors.primary, fontSize: '20px', marginBottom: '15px' }}>
                <CheckCircle size={18} style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle', color: colors.success }} /> File Loaded: {importFile.name}
              </h3>
              <p style={{ color: theme.textSecondary }}>
                Found {importPreview.length} rows. Map your CSV columns to customer fields below.
              </p>
            </div>

            {/* Column Mapping */}
            <div style={{ 
              ...theme.glass, 
              padding: '25px', 
              borderRadius: '12px', 
              marginBottom: '30px' 
            }}>
              <h4 style={{ color: theme.text, marginBottom: '20px', fontSize: '18px' }}>
                🔗 Column Mapping
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '15px' 
              }}>
                {[
                  { key: 'firstName', label: 'First Name *', required: true },
                  { key: 'lastName', label: 'Last Name *', required: true },
                  { key: 'email', label: 'Email' },
                  { key: 'phone', label: 'Phone' },
                  { key: 'address', label: 'Address' },
                  { key: 'city', label: 'City' },
                  { key: 'state', label: 'State' },
                  { key: 'zip', label: 'Zip Code' },
                  { key: 'vehicleYear', label: 'Vehicle Year' },
                  { key: 'vehicleMake', label: 'Vehicle Make' },
                  { key: 'vehicleModel', label: 'Vehicle Model' },
                  { key: 'vehicleVIN', label: 'Vehicle VIN' },
                  { key: 'vehicleMileage', label: 'Vehicle Mileage' }
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ 
                      display: 'block', 
                      color: field.required ? colors.primary : theme.textSecondary, 
                      fontSize: '13px', 
                      marginBottom: '6px',
                      fontWeight: field.required ? '600' : '500'
                    }}>
                      {field.label}
                    </label>
                    <select
                      value={columnMapping[field.key] || ''}
                      onChange={(e) => setColumnMapping({ ...columnMapping, [field.key]: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: field.required && !columnMapping[field.key] 
                          ? '2px solid #ef4444' 
                          : '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: theme.text,
                        fontSize: '14px'
                      }}
                    >
                      <option value="">-- Select Column --</option>
                      {Object.keys(importPreview[0] || {}).map(header => (
                        <option key={header} value={header}>{header}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Data Preview */}
            <div style={{ 
              ...theme.glass, 
              padding: '25px', 
              borderRadius: '12px', 
              marginBottom: '30px' 
            }}>
              <h4 style={{ color: theme.text, marginBottom: '15px', fontSize: '18px' }}>
                👁️ Data Preview (First 5 Rows)
              </h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse', 
                  fontSize: '13px' 
                }}>
                  <thead>
                    <tr style={{ background: 'rgba(102, 126, 234, 0.2)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: '600' }}>First Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: '600' }}>Last Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: '600' }}>Email</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: '600' }}>Phone</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: '600' }}>City</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: theme.text, fontWeight: '600' }}>Vehicle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.slice(0, 5).map((row, idx) => (
                      <tr key={`import-preview-${idx}-${row[0] || row[Object.keys(row)[0]] || idx}`} style={{ 
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        background: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                      }}>
                        <td style={{ padding: '12px', color: theme.text }}>
                          {row[columnMapping.firstName] || '-'}
                        </td>
                        <td style={{ padding: '12px', color: theme.text }}>
                          {row[columnMapping.lastName] || '-'}
                        </td>
                        <td style={{ padding: '12px', color: theme.textSecondary }}>
                          {row[columnMapping.email] || '-'}
                        </td>
                        <td style={{ padding: '12px', color: theme.textSecondary }}>
                          {row[columnMapping.phone] || '-'}
                        </td>
                        <td style={{ padding: '12px', color: theme.textSecondary }}>
                          {row[columnMapping.city] || 'Oklahoma City'}
                        </td>
                        <td style={{ padding: '12px', color: theme.textSecondary }}>
                          {columnMapping.vehicleYear && row[columnMapping.vehicleYear]
                            ? `${row[columnMapping.vehicleYear]} ${row[columnMapping.vehicleMake] || ''} ${row[columnMapping.vehicleModel] || ''}`
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Import Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                onClick={() => {
                  setImportFile(null);
                  setImportPreview([]);
                }}
                style={{
                  padding: '12px 24px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '10px',
                  color: theme.text,
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                ← Choose Different File
              </button>

              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                {(!columnMapping.firstName || !columnMapping.lastName) && (
                  <div style={{ 
                    color: '#ef4444', 
                    fontSize: '14px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px' 
                  }}>
                    <AlertCircle size={18} />
                    Map required fields to continue
                  </div>
                )}

                <button
                  onClick={onImport}
                  disabled={isImporting || !columnMapping.firstName || !columnMapping.lastName}
                  style={{
                    padding: '14px 32px',
                    background: isImporting || !columnMapping.firstName || !columnMapping.lastName
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    color: 'white',
                    cursor: isImporting || !columnMapping.firstName || !columnMapping.lastName ? 'not-allowed' : 'pointer',
                    fontWeight: '700',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    opacity: isImporting || !columnMapping.firstName || !columnMapping.lastName ? 0.5 : 1
                  }}
                >
                  {isImporting ? (
                    <>
                      <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Importing...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Import {importPreview.length} Customers
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

